import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';

interface OptimisticContext<TCache> {
  previous: TCache | undefined;
}

export interface UseOptimisticMutationOptions<TData, TVariables, TCache> extends Omit<
  UseMutationOptions<TData, Error, TVariables, OptimisticContext<TCache>>,
  'onMutate'
> {
  /** Key of the cached query the mutation patches and later invalidates. */
  queryKey: QueryKey;
  /** Pure cache patch applied optimistically before the request settles. */
  update: (cache: TCache, variables: TVariables) => TCache;
}

/**
 * `useMutation` with the standard optimistic-update contract wired in:
 * `onMutate` cancels in-flight fetches, snapshots the cache and applies
 * `update`; `onError` rolls back to the snapshot; `onSettled` invalidates so
 * the server reconciles the cache. Caller-supplied `onError`/`onSettled`
 * handlers run after the built-in rollback/invalidation.
 */
export function useOptimisticMutation<TData, TVariables, TCache>({
  queryKey,
  update,
  ...options
}: UseOptimisticMutationOptions<TData, TVariables, TCache>): UseMutationResult<
  TData,
  Error,
  TVariables,
  OptimisticContext<TCache>
> {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TCache>(queryKey);
      queryClient.setQueryData<TCache>(queryKey, (cache) =>
        cache === undefined ? cache : update(cache, variables),
      );
      return { previous };
    },
    onError: (error, variables, snapshot, context) => {
      if (snapshot?.previous !== undefined) {
        queryClient.setQueryData(queryKey, snapshot.previous);
      }
      return options.onError?.(error, variables, snapshot, context);
    },
    onSettled: (data, error, variables, snapshot, context) => {
      void queryClient.invalidateQueries({ queryKey });
      return options.onSettled?.(data, error, variables, snapshot, context);
    },
  });
}
